import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useForm } from 'react-hook-form';
import { 
  Users, 
  Camera, 
  Flame, 
  TrendingUp, 
  Plus, 
  Settings, 
  RefreshCw,
  UserCheck,
  UserX,
  Eye,
  EyeOff,
  Search,
  BarChart3,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Edit,
  Trash2,
  Key
} from 'lucide-react';
import { apiService } from '../services/apiService';
import LoadingSpinner from '../components/UI/LoadingSpinner';
import toast from 'react-hot-toast';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const AdminPage = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedInterval, setSelectedInterval] = useState('300');
  const queryClient = useQueryClient();
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showEditUser, setShowEditUser] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingUser, setDeletingUser] = useState(null);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [newTokenName, setNewTokenName] = useState('');
  const [newTokenScope, setNewTokenScope] = useState('fires:read');
  const [createdToken, setCreatedToken] = useState(null);

  // Fetch admin data
  const { data: fireStats, isLoading: fireStatsLoading } = useQuery(
    'admin-fire-stats',
    () => apiService.getFireStats(),
    { refetchInterval: 60000 }
  );

  const { data: userStats, isLoading: userStatsLoading } = useQuery(
    'admin-user-stats',
    () => apiService.getUserStats(),
    { refetchInterval: 60000 }
  );

  const { data: users, isLoading: usersLoading } = useQuery('admin-users', () => apiService.getUsers());
  const { data: syncInterval, isLoading: intervalLoading } = useQuery('sync-interval', () => apiService.getSyncInterval());

  const { data: photosData, isLoading: photosLoading } = useQuery(
    'admin-photos',
    () => apiService.getPhotos({ limit: 20 })
  );

  const { register, handleSubmit, formState: { errors }, reset } = useForm();
  const { register: registerEdit, handleSubmit: handleSubmitEdit, reset: resetEdit, setValue: setValueEdit, formState: { errors: errorsEdit } } = useForm();

  // Mutations
  const syncFiresMutation = useMutation(
    (useActiveEndpoint = true) => apiService.syncFires(useActiveEndpoint),
    {
      onSuccess: () => {
        toast.success('Incêndios sincronizados com sucesso!');
        queryClient.invalidateQueries('fires');
        queryClient.invalidateQueries('admin-fire-stats');
        queryClient.invalidateQueries('admin-user-stats');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Erro ao sincronizar incêndios');
      }
    }
  );

  const updateIntervalMutation = useMutation(apiService.updateSyncInterval, {
    onSuccess: () => {
      toast.success('Intervalo de sincronização atualizado!');
      queryClient.invalidateQueries('sync-interval');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Erro ao atualizar intervalo');
    }
  });

  // Toggle user status mutation
  const toggleUserMutation = useMutation(
    (userId) => apiService.toggleUserStatus(userId),
    {
      onSuccess: () => {
        toast.success('Estado do utilizador atualizado!');
        queryClient.invalidateQueries('users');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Erro ao atualizar estado');
      }
    }
  );

  // Update user mutation
  const updateUserMutation = useMutation(
    ({ userId, userData }) => apiService.updateUser(userId, userData),
    {
      onSuccess: () => {
        toast.success('Utilizador atualizado com sucesso!');
        queryClient.invalidateQueries('users');
        setShowEditUser(false);
        setEditingUser(null);
        resetEdit();
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Erro ao atualizar utilizador');
      }
    }
  );

  // Delete user mutation
  const deleteUserMutation = useMutation(
    (userId) => apiService.deleteUser(userId),
    {
      onSuccess: (data) => {
        toast.success(data.message || 'Utilizador apagado com sucesso!');
        queryClient.invalidateQueries('users');
        setShowDeleteConfirm(false);
        setDeletingUser(null);
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Erro ao apagar utilizador');
      }
    }
  );

  // Reset password mutation
  const resetPasswordMutation = useMutation(
    ({ userId, newPassword }) => apiService.resetUserPassword(userId, newPassword),
    {
      onSuccess: (data) => {
        toast.success(data.message || 'Palavra-passe redefinida com sucesso!');
        setShowPasswordReset(false);
        setResetPasswordUser(null);
        setNewPassword('');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Erro ao redefinir palavra-passe');
      }
    }
  );

  // Set selected interval when sync interval data loads
  React.useEffect(() => {
    if (syncInterval?.interval_seconds) {
      setSelectedInterval(syncInterval.interval_seconds.toString());
    }
  }, [syncInterval]);

  const onCreateUser = async (data) => {
    try {
      setIsCreating(true);
      await apiService.createUser(data);
      reset();
      setShowCreateUser(false);
      queryClient.invalidateQueries('admin-users');
      toast.success('Utilizador criado com sucesso!');
    } catch (error) {
      const message = error.response?.data?.error || 'Erro ao criar utilizador';
      toast.error(message);
    } finally {
      setIsCreating(false);
    }
  };

  const handleToggleUserStatus = (userId) => {
    toggleUserMutation.mutate(userId);
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setValueEdit('name', user.name);
    setValueEdit('surname', user.surname);
    setValueEdit('entity', user.entity || '');
    setValueEdit('email', user.email);
    setValueEdit('role', user.role);
    setValueEdit('is_active', user.is_active);
    setShowEditUser(true);
  };

  const handleUpdateUser = (data) => {
    updateUserMutation.mutate({
      userId: editingUser.id,
      userData: {
        name: data.name,
        surname: data.surname,
        entity: data.entity || null,
        email: data.email,
        role: data.role,
        is_active: data.is_active
      }
    });
  };

  const handleDeleteUser = (user) => {
    setDeletingUser(user);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteUser = () => {
    if (deletingUser) {
      deleteUserMutation.mutate(deletingUser.id);
    }
  };

  const handleResetPassword = (user) => {
    setResetPasswordUser(user);
    setNewPassword('');
    setShowNewPassword(false);
    setShowPasswordReset(true);
  };

  const confirmResetPassword = () => {
    if (resetPasswordUser && newPassword.length >= 8) {
      resetPasswordMutation.mutate({
        userId: resetPasswordUser.id,
        newPassword: newPassword
      });
    }
  };

  const handleSyncFires = () => {
    syncFiresMutation.mutate(true); // Pass useActiveEndpoint parameter
  };

  const handleUpdateInterval = () => {
    const intervalSeconds = parseInt(selectedInterval);
    updateIntervalMutation.mutate(intervalSeconds);
  };

  const getIntervalLabel = (seconds) => {
    const minutes = seconds / 60;
    if (minutes < 60) {
      return `${minutes} minuto${minutes > 1 ? 's' : ''}`;
    }
    const hours = minutes / 60;
    return `${hours} hora${hours > 1 ? 's' : ''}`;
  };

  const { data: apiTokensData, isLoading: apiTokensLoading } = useQuery(
    'admin-api-tokens',
    () => apiService.getApiTokens(),
    {
      enabled: activeTab === 'apiTokens'
    }
  )

  const createApiTokenMutation = useMutation(
    (payload) => apiService.createApiToken(payload),
    {
      onSuccess: (data) => {
        toast.success('Token criado com sucesso!');
        setCreatedToken(data.token);
        setNewTokenName('');
        setNewTokenScope('fires:read');
        queryClient.invalidateQueries('admin-api-tokens');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Erro ao criar token');
      }
    }
  );

  const revokeApiTokenMutation = useMutation(
    (tokenId) => apiService.revokeApiToken(tokenId),
    {
      onSuccess: () => {
        toast.success('Token revogado com sucesso!');
        queryClient.invalidateQueries('admin-api-tokens');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Erro ao revogar token');
      }
    }
  );
  
  const handleCreateApiToken = (e) => {
    e.preventDefault();

    if (!newTokenName.trim()) {
      toast.error('O nome do token é obrigatório');
      return;
    }

    setCreatedToken(null);

    createApiTokenMutation.mutate({
      name: newTokenName.trim(),
      scope: newTokenScope
    });
  };

  const handleRevokeApiToken = (tokenId) => {
    if (window.confirm('Tem certeza que deseja revogar este token?')) {
      revokeApiTokenMutation.mutate(tokenId);
    }
  };

  const StatCard = ({ title, value, icon: Icon, color = 'primary', isLoading = false }) => {
    const colorClasses = {
      primary: 'bg-primary-500',
      fire: 'bg-fire-500',
      blue: 'bg-blue-500',
      green: 'bg-green-500',
      yellow: 'bg-yellow-500',
    };

    return (
      <div className="card p-6">
        <div className="flex items-center">
          <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
            <Icon className="w-6 h-6 text-white" />
          </div>
          <div className="ml-4">
            <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
              {title}
            </p>
            {isLoading ? (
              <LoadingSpinner size="sm" />
            ) : (
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {value}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  };

  const tabs = [
    { id: 'overview', name: 'Visão Geral' },
    { id: 'users', name: 'Utilizadores' },
    { id: 'photos', name: 'Fotos' },
    { id: 'system', name: 'Sistema' },
    { id: 'apiTokens', name: 'API Tokens'}
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Administração
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Painel de controlo da plataforma FIREWATCH Portugal
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatCard
              title="Total de Utilizadores"
              value={userStats?.total_users || 0}
              icon={Users}
              color="blue"
              isLoading={userStatsLoading}
            />
            <StatCard
              title="Utilizadores Ativos"
              value={userStats?.active_users || 0}
              icon={UserCheck}
              color="green"
              isLoading={userStatsLoading}
            />
            <StatCard
              title="Incêndios Ativos"
              value={fireStats?.active_fires || 0}
              icon={Flame}
              color="fire"
              isLoading={fireStatsLoading}
            />
            <StatCard
              title="Total de Fotos"
              value={photosData?.total || 0}
              icon={Camera}
              color="primary"
              isLoading={photosLoading}
            />
          </div>

          {/* Recent Activity */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="card p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Estatísticas de Incêndios
              </h3>
              {fireStatsLoading ? (
                <LoadingSpinner />
              ) : (
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Importantes:</span>
                    <span className="font-medium">{fireStats?.important_fires || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Recentes:</span>
                    <span className="font-medium">{fireStats?.recent_fires || 0}</span>
                  </div>
                </div>
              )}
            </div>

            <div className="card p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Estatísticas de Utilizadores
              </h3>
              {userStatsLoading ? (
                <LoadingSpinner />
              ) : (
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Com fotos:</span>
                    <span className="font-medium">{userStats?.users_with_photos || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Novos (30 dias):</span>
                    <span className="font-medium">{userStats?.recent_users || 0}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Gestão de Utilizadores
            </h2>
            <button
              onClick={() => setShowCreateUser(true)}
              className="btn-primary flex items-center space-x-2"
            >
              <Plus className="w-4 h-4" />
              <span>Criar Utilizador</span>
            </button>
          </div>

          {/* Create User Modal */}
          {showCreateUser && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 w-full max-w-md border border-gray-200 dark:border-gray-700">
                <div className="text-center mb-8">
                  <div className="flex justify-center mb-4">
                    <div className="w-16 h-16 flex items-center justify-center bg-primary-50 dark:bg-primary-900/20 rounded-2xl">
                      <img 
                        src="/firewatch_logo_transparent.png" 
                        alt="FIREWATCH Logo" 
                        className="w-12 h-12 object-contain"
                      />
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                    Criar Novo Utilizador
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Adicionar um novo utilizador ao FIREWATCH
                  </p>
                </div>
                
                <form onSubmit={handleSubmit(onCreateUser)} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="form-label">Nome *</label>
                      <input
                        type="text"
                        className="form-input"
                        {...register('name', { required: 'Nome é obrigatório' })}
                      />
                      {errors.name && (
                        <p className="text-sm text-red-600 mt-1">{errors.name.message}</p>
                      )}
                    </div>
                    <div>
                      <label className="form-label">Apelido *</label>
                      <input
                        type="text"
                        className="form-input"
                        {...register('surname', { required: 'Apelido é obrigatório' })}
                      />
                      {errors.surname && (
                        <p className="text-sm text-red-600 mt-1">{errors.surname.message}</p>
                      )}
                    </div>
                  </div>
                  
                  <div>
                    <label className="form-label">Email *</label>
                    <input
                      type="email"
                      className="form-input"
                      {...register('email', { required: 'Email é obrigatório' })}
                    />
                    {errors.email && (
                      <p className="text-sm text-red-600 mt-1">{errors.email.message}</p>
                    )}
                  </div>
                  
                  <div>
                    <label className="form-label">Entidade</label>
                    <input
                      type="text"
                      className="form-input"
                      {...register('entity')}
                    />
                  </div>
                  
                  <div>
                    <label className="form-label">Password *</label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        className="form-input pr-10"
                        {...register('password', { 
                          required: 'Password é obrigatória',
                          minLength: { value: 8, message: 'Mínimo 8 caracteres' }
                        })}
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    {errors.password && (
                      <p className="text-sm text-red-600 mt-1">{errors.password.message}</p>
                    )}
                  </div>
                  
                  <div>
                    <label className="form-label">Confirmar Password *</label>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      className="form-input"
                      {...register('confirm_password', { 
                        required: 'Confirmação é obrigatória'
                      })}
                    />
                    {errors.confirm_password && (
                      <p className="text-sm text-red-600 mt-1">{errors.confirm_password.message}</p>
                    )}
                  </div>
                  
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="is_admin"
                      className="rounded border-gray-300"
                      {...register('is_admin')}
                    />
                    <label htmlFor="is_admin" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                      Administrador
                    </label>
                  </div>
                  
                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowCreateUser(false)}
                      className="btn-secondary"
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      disabled={isCreating}
                      className="btn-primary flex items-center space-x-2"
                    >
                      {isCreating && <LoadingSpinner size="sm" />}
                      <span>{isCreating ? 'A criar...' : 'Criar'}</span>
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Users List */}
          {usersLoading ? (
            <LoadingSpinner />
          ) : (
            <div className="card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Utilizador
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Papel
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Estado
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {users?.users.map((user) => (
                      <tr key={user.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              {user.name} {user.surname}
                            </div>
                            {user.entity && (
                              <div className="text-sm text-gray-500 dark:text-gray-400">
                                {user.entity}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          {user.email}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`status-badge ${user.role === 'admin' ? 'status-important' : 'status-active'}`}>
                            {user.role === 'admin' ? 'Admin' : 'Utilizador'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`status-badge ${user.is_active ? 'status-active' : 'status-inactive'}`}>
                            {user.is_active ? 'Ativo' : 'Inativo'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-3">
                            <button
                              onClick={() => handleEditUser(user)}
                              className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 flex items-center space-x-1"
                              title="Editar utilizador"
                            >
                              <Edit size={16} />
                              <span>Editar</span>
                            </button>
                            <button
                              onClick={() => handleToggleUserStatus(user.id)}
                              className={`flex items-center space-x-1 ${
                                user.is_active 
                                  ? 'text-orange-600 hover:text-orange-900 dark:text-orange-400 dark:hover:text-orange-300' 
                                  : 'text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300'
                              }`}
                              title={user.is_active ? 'Desativar utilizador' : 'Ativar utilizador'}
                            >
                              {user.is_active ? <XCircle size={16} /> : <CheckCircle size={16} />}
                              <span>{user.is_active ? 'Desativar' : 'Ativar'}</span>
                            </button>
                            <button
                              onClick={() => handleResetPassword(user)}
                              className="text-yellow-600 hover:text-yellow-900 dark:text-yellow-400 dark:hover:text-yellow-300 flex items-center space-x-1"
                              title="Redefinir palavra-passe"
                            >
                              <Key size={16} />
                              <span>Reset</span>
                            </button>
                            <button
                              onClick={() => handleDeleteUser(user)}
                              className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 flex items-center space-x-1"
                              title="Apagar utilizador"
                            >
                              <Trash2 size={16} />
                              <span>Apagar</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* System Tab */}
      {activeTab === 'system' && (
        <div className="space-y-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Configurações do Sistema
          </h2>
          
          <div className="card p-6">
            <h3 className="text-md font-medium text-gray-900 dark:text-white mb-4">
              Sincronização de Dados
            </h3>
            
            {/* Manual Sync */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Sincronizar incêndios com a API Fogos.pt
                </p>
              </div>
              <button
                onClick={handleSyncFires}
                className="btn-primary flex items-center space-x-2"
              >
                <RefreshCw className="w-4 h-4" />
                <span>Sincronizar Agora</span>
              </button>
            </div>
            
            {/* Auto Sync Settings */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
                Sincronização Automática
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Intervalo de Atualização
                  </label>
                  <select 
                    className="form-input"
                    value={selectedInterval}
                    onChange={(e) => setSelectedInterval(e.target.value)}
                    disabled={intervalLoading}
                  >
                    <option value="300">5 minutos (recomendado)</option>
                    <option value="600">10 minutos</option>
                    <option value="900">15 minutos</option>
                    <option value="1800">30 minutos</option>
                    <option value="3600">1 hora</option>
                  </select>
                </div>
                <div className="flex items-end">
                  <button 
                    className="btn-secondary w-full"
                    onClick={handleUpdateInterval}
                    disabled={updateIntervalMutation.isLoading || intervalLoading}
                  >
                    {updateIntervalMutation.isLoading ? 'A guardar...' : 'Guardar Configuração'}
                  </button>
                </div>
              </div>
              {syncInterval && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  {syncInterval.is_running ? '✅' : '⚠️'} Sincronização automática {syncInterval.is_running ? 'ativa' : 'inativa'}: a cada {getIntervalLabel(syncInterval.interval_seconds)}
                </p>
              )}
            </div>
          </div>
        </div>
      )}


      {activeTab === 'apiTokens' && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Gestão de API Tokens
            </h2>
          </div>

          <div className="card p-6">
            <h3 className="text-md font-medium text-gray-900 dark:text-white mb-4">
              Criar Novo Token
            </h3>

            <form onSubmit={handleCreateApiToken} className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="form-label">Nome *</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Ex: ANEPC"
                  value={newTokenName}
                  onChange={(e) => setNewTokenName(e.target.value)}
                />
              </div>

              <div>
                <label className="form-label">Scope</label>
                <select
                  className="form-input"
                  value={newTokenScope}
                  onChange={(e) => setNewTokenScope(e.target.value)}
                >
                  <option value="fires:read">fires:read</option>
                </select>
              </div>

              <div className="flex items-end">
                <button
                  type="submit"
                  disabled={createApiTokenMutation.isLoading}
                  className="btn-primary w-full flex items-center justify-center space-x-2"
                >
                  {createApiTokenMutation.isLoading && <LoadingSpinner size="sm" />}
                  <span>{createApiTokenMutation.isLoading ? 'A criar...' : 'Criar Token'}</span>
                </button>
              </div>
            </form>

            {createdToken && (
              <div className="mt-6 rounded-lg border border-yellow-300 bg-yellow-50 dark:bg-yellow-900/20 dark:border-yellow-700 p-4">
                <div className="flex items-start space-x-3">
                  <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-medium text-yellow-800 dark:text-yellow-300">
                      Token criado com sucesso
                    </p>
                    <p className="text-sm text-yellow-700 dark:text-yellow-400 mt-1">
                      Guarde este token agora. Ele só será mostrado uma única vez.
                    </p>
                    <div className="mt-3 p-3 rounded bg-white dark:bg-gray-800 border border-yellow-200 dark:border-yellow-700 break-all text-sm font-mono text-gray-900 dark:text-white">
                      {createdToken}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {apiTokensLoading ? (
            <LoadingSpinner />
          ) : (
            <div className="card overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-md font-medium text-gray-900 dark:text-white">
                  Tokens Existentes
                </h3>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Nome
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Scope
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Estado
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Criado
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Última Utilização
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Expira
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                        Ações
                      </th>
                    </tr>
                  </thead>

                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                    {(apiTokensData?.tokens || []).length === 0 ? (
                      <tr>
                        <td colSpan="7" className="px-6 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                          Ainda não existem tokens criados.
                        </td>
                      </tr>
                    ) : (
                      (apiTokensData?.tokens || []).map((token) => (
                        <tr key={token.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                            {token.name}
                          </td>

                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                            {token.scope}
                          </td>

                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`status-badge ${token.is_active ? 'status-active' : 'status-inactive'}`}>
                              {token.is_active ? 'Ativo' : 'Revogado'}
                            </span>
                          </td>

                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                            {token.created_at
                              ? formatDistanceToNow(new Date(token.created_at), {
                                  addSuffix: true,
                                  locale: ptBR
                                })
                              : '-'}
                          </td>

                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                            {token.last_used_at
                              ? formatDistanceToNow(new Date(token.last_used_at), {
                                  addSuffix: true,
                                  locale: ptBR
                                })
                              : 'Nunca'}
                          </td>

                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                            {token.expires_at
                              ? formatDistanceToNow(new Date(token.expires_at), {
                                  addSuffix: true,
                                  locale: ptBR
                                })
                              : 'Sem expiração'}
                          </td>

                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            {token.is_active ? (
                              <button
                                onClick={() => handleRevokeApiToken(token.id)}
                                className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 flex items-center space-x-1"
                                title="Revogar token"
                              >
                                <XCircle size={16} />
                                <span>Revogar</span>
                              </button>
                            ) : (
                              <span className="text-gray-400 dark:text-gray-500">—</span>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Edit User Modal */}
      {showEditUser && editingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 w-full max-w-md max-h-[90vh] overflow-y-auto border border-gray-200 dark:border-gray-700">
            <div className="text-center mb-8">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 flex items-center justify-center bg-primary-50 dark:bg-primary-900/20 rounded-2xl">
                  <img 
                    src="/firewatch_logo_transparent.png" 
                    alt="FIREWATCH Logo" 
                    className="w-12 h-12 object-contain"
                  />
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                Editar Utilizador
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {editingUser.name} {editingUser.surname}
              </p>
            </div>
            
            <form onSubmit={handleSubmitEdit(handleUpdateUser)} className="space-y-4">
              <div>
                <label className="form-label">
                  Nome *
                </label>
                <input
                  type="text"
                  {...registerEdit('name', { required: 'Nome é obrigatório' })}
                  className="form-input"
                  placeholder="Nome do utilizador"
                />
                {errorsEdit.name && (
                  <p className="text-red-500 text-sm mt-1">{errorsEdit.name.message}</p>
                )}
              </div>

              <div>
                <label className="form-label">
                  Apelido *
                </label>
                <input
                  type="text"
                  {...registerEdit('surname', { required: 'Apelido é obrigatório' })}
                  className="form-input"
                  placeholder="Apelido do utilizador"
                />
                {errorsEdit.surname && (
                  <p className="text-red-500 text-sm mt-1">{errorsEdit.surname.message}</p>
                )}
              </div>

              <div>
                <label className="form-label">
                  Entidade
                </label>
                <input
                  type="text"
                  {...registerEdit('entity')}
                  className="form-input"
                  placeholder="Entidade/Organização (opcional)"
                />
              </div>

              <div>
                <label className="form-label">
                  Email *
                </label>
                <input
                  type="email"
                  {...registerEdit('email', { 
                    required: 'Email é obrigatório',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Email inválido'
                    }
                  })}
                  className="form-input"
                  placeholder="email@exemplo.com"
                />
                {errorsEdit.email && (
                  <p className="text-red-500 text-sm mt-1">{errorsEdit.email.message}</p>
                )}
              </div>

              <div>
                <label className="form-label">
                  Papel
                </label>
                <select {...registerEdit('role')} className="form-input">
                  <option value="user">Utilizador</option>
                  <option value="admin">Administrador</option>
                </select>
              </div>

              <div className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <input
                  type="checkbox"
                  id="edit_is_active"
                  {...registerEdit('is_active')}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label htmlFor="edit_is_active" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Utilizador Ativo
                </label>
              </div>
              
              <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-600">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditUser(false);
                    setEditingUser(null);
                    resetEdit();
                  }}
                  className="btn-secondary"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={updateUserMutation.isLoading}
                  className="btn-primary flex items-center space-x-2"
                >
                  {updateUserMutation.isLoading && <LoadingSpinner size="sm" />}
                  <span>{updateUserMutation.isLoading ? 'A atualizar...' : 'Guardar Alterações'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Password Reset Modal */}
      {showPasswordReset && resetPasswordUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 w-full max-w-md border border-gray-200 dark:border-gray-700">
            <div className="text-center mb-8">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 flex items-center justify-center bg-yellow-50 dark:bg-yellow-900/20 rounded-2xl">
                  <Key className="h-8 w-8 text-yellow-600 dark:text-yellow-400" />
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                Redefinir Palavra-passe
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {resetPasswordUser.name} {resetPasswordUser.surname}
              </p>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="form-label">
                  Nova Palavra-passe *
                </label>
                <div className="relative">
                  <input
                    type={showNewPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="form-input pr-10"
                    placeholder="Mínimo 8 caracteres"
                    minLength={8}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                  >
                    {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {newPassword.length > 0 && newPassword.length < 8 && (
                  <p className="text-red-500 text-sm mt-1">A palavra-passe deve ter pelo menos 8 caracteres</p>
                )}
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-600">
              <button
                type="button"
                onClick={() => {
                  setShowPasswordReset(false);
                  setResetPasswordUser(null);
                  setNewPassword('');
                }}
                className="btn-secondary"
              >
                Cancelar
              </button>
              <button
                onClick={confirmResetPassword}
                disabled={resetPasswordMutation.isLoading || newPassword.length < 8}
                className="bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 disabled:opacity-50"
              >
                {resetPasswordMutation.isLoading && <LoadingSpinner size="sm" />}
                <span>{resetPasswordMutation.isLoading ? 'A redefinir...' : 'Redefinir Palavra-passe'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && deletingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 w-full max-w-md border border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-3 mb-4">
              <div className="flex-shrink-0">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Confirmar Eliminação
                </h3>
              </div>
            </div>
            
            <div className="mb-6">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Tem a certeza que pretende apagar o utilizador{' '}
                <span className="font-semibold text-gray-900 dark:text-white">
                  {deletingUser.name} {deletingUser.surname}
                </span>?
              </p>
              <p className="text-sm text-orange-600 dark:text-orange-400 mt-2">
                Esta ação não pode ser desfeita. As fotos do utilizador serão transferidas para a sua conta.
              </p>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeletingUser(null);
                }}
                className="btn-secondary"
              >
                Cancelar
              </button>
              <button
                onClick={confirmDeleteUser}
                disabled={deleteUserMutation.isLoading}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 disabled:opacity-50"
              >
                {deleteUserMutation.isLoading && <LoadingSpinner size="sm" />}
                <span>{deleteUserMutation.isLoading ? 'A apagar...' : 'Apagar'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPage;
